<template>
  <div
    class="line-item"
    :class="{
      [$class('loading')]: !lineItem,
      [$class('updating')]:
        state === 'updating' ||
        (isDeposit && depositItemUpdate.length > 0) ||
        itemDepositUpdating ||
        itemBundleUpdating,
      'line-item--studioshot': lineItem?.tags?.includes('studioshot'),
    }"
  >
    <div class="line-item__grid">
      <a class="line-item__link" :href="lineItem.url" tabindex="-1">
        <span class="visually-hidden" v-text="lineItem.product_title" />

        <div class="line-item__image-container">
          <responsive-image
            class="line-item__image"
            :image="lineItem.localized_image ?? lineItem.featured_image"
            :lazy-load="false"
            :ratio="$variable('product.imageRatio')"
            sizes="(min-width: 1024px) 107px, 101px"
            widths="107, 214, 321"
          />
        </div>
      </a>

      <div class="line-item__body">
        <div class="line-item__details">
          <a
            class="line-item__link line-item__title text-mobile-paragraph-m-bold text-desktop-paragraph-m-bold"
            :href="lineItem.url"
            v-text="lineItem.product_title"
          />

          <p
            v-if="
              !lineItem.product_has_only_default_variant || lineItem.gift_card
            "
            class="line-item__variant line-item__truncate text-mobile-paragraph-s-regular text-desktop-paragraph-s-regular"
          >
            <span v-text="variantText" />
            <span v-if="bundleExtraItemName">, </span>

            <span v-if="bundleExtraItemName" v-text="bundleExtraItemName" />
          </p>
          <p
            v-if="lineItemProperties"
            class="line-item__line-item-properties text-mobile-paragraph-s-regular text-desktop-paragraph-s-regular"
            v-html="lineItemProperties"
          />

          <p class="line-item__price-container">
            <product-prices
              class="line-item__price"
              :compare-at-price="compareAtPrice"
              element="span"
              :price="lineItem?.final_price"
              template="cart"
              :variant-id="lineItem?.variant_id"
              :unit-price="lineItem?.unit_price"
              show-savings-notification
              :unit-price-measurement="
                lineItem?.unit_price_measurement?.reference_unit
              "
            />
          </p>
        </div>

        <div
          class="line-item__actions"
          :class="{
            'line-item__actions--graduated':
              lineItem.graduated_discount_products > 0,
          }"
        >
          <quantity-selector
            class="line-item__quantity-selector"
            :line-item="lineItem"
            :quantity="quantity"
            :state="state"
            :deposit="isDeposit"
            :item-dependencies-updating="
              itemDepositUpdating || itemBundleUpdating
            "
            @update-quantity="handleQuantityUpdate"
          />

          <button
            v-if="!isDeposit"
            class="line-item__remove"
            :class="{ [$class('disabled')]: state === 'updating' }"
            type="button"
            @click="handleRemove"
          >
            <span
              class="visually-hidden"
              v-text="$string('cart.line_item.remove')"
            />

            <icon-bin />
          </button>
        </div>
      </div>
    </div>

    <notification-panel
      v-if="notification.display"
      class-container="line-item__notification-panel"
      :show-close="false"
      :text="notification.text"
      :title="notification.title"
      type="error"
    />
  </div>
</template>

<script>
/**
 * Vue: Line item (line-item)
 * -----------------------------------------------------------------------------
 * Displays line items in cart, cart drawer, and order.
 *
 * @param {Boolean|Object} lineItem - Line item in cart response or order.
 *
 */
import { mapActions, mapState } from 'pinia';

import NotificationPanel from '~async/utils/notification-panel/notification-panel';
import ProductPrices from '~global/product-prices/product-prices';
import QuantitySelector from '~async/quantity-selector/quantity-selector';
import ResponsiveImage from '~global/images/responsive-image';

import IconBin from '~icons/general/bin.svg';

import { computeDiscountLogic } from '~/helpers/vue';
import { getMaxQuantityErrorMessage } from '~/helpers/general';

import { formatTrackingItem } from '~/format/format-tracking-item';
import useDiscountCampaignsStore from '~stores/discount-campaigns';
import useCartStore from '~stores/cart';
import useToastNotificationsStore from '~stores/toast-notifications';

export default {
  name: 'LineItem',

  components: {
    IconBin,
    NotificationPanel,
    ProductPrices,
    QuantitySelector,
    ResponsiveImage,
  },

  props: {
    lineItem: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    location: {
      type: String,
      default: 'cart-drawer',
    },
  },

  data() {
    return {
      error: '',
      quantity: 1,
      state: 'loading',
    };
  },

  computed: {
    ...mapState(useDiscountCampaignsStore, {
      getCampaignDiscount: 'getCampaignDiscount',
    }),
    ...mapState(useCartStore, {
      cart: 'response',
      cartStatus: 'status',
      bundleItemUpdate: 'bundleItemUpdate',
      depositItemUpdate: 'depositItemUpdate',
    }),

    /**
     * Checks whether line item is deposit product.
     */
    isDeposit() {
      return (
        `${this.lineItem.id}` === this.$variable('product.depositProductID')
      );
    },

    itemDepositUpdating() {
      return (
        this.lineItem.can_deposit &&
        this.depositItemUpdate.includes(this.lineItem.variant_id)
      );
    },

    itemBundleUpdating() {
      return this.bundleItemUpdate.includes(this.lineItem.id);
    },

    /**
     * Compute compare at price.
     * - Uses compare at price if it exists.
     * - Otherwise if script discount has been applied use original price.
     * - This way it always shows the largest original price.
     * - Otherwise show no compare at price.
     * @returns {Number|Boolean}
     */
    compareAtPrice() {
      if (this.lineItem?.compare_at_price) {
        return this.lineItem?.compare_at_price;
      }

      if (this.lineItem?.original_price !== this.lineItem?.final_price) {
        return this.lineItem?.original_price;
      }

      return false;
    },

    /**
     * Compute if line item is limited by inventory quantity.
     * @returns {Boolean}
     */
    limitedInventory() {
      return (
        this.lineItem.inventory_management &&
        this.lineItem.inventory_policy === 'deny'
      );
    },

    /**
     * Compute line item properties based on theme setting.
     * - Only display those in setting in the order they're added.
     * - Won't display line item if value is blank or false.
     * @returns {String}
     */
    lineItemProperties() {
      const propertiesSetting = this.$variable('settings.lineItemProperties')
        ? this.$variable('settings.lineItemProperties').trim()
        : '';

      if (
        !propertiesSetting ||
        !this.lineItem.properties ||
        !Object.keys(this.lineItem.properties).length
      ) {
        return '';
      }

      // Translation map for property names
      const hiddenPropertiesMappedTranslations = {};

      const translationMap = {
        'Recipient name': 'cart.line_item.recipient_name',
        'Recipient email': 'cart.line_item.recipient_email',
        Message: 'cart.line_item.message',
        'Send on': 'cart.line_item.send_on',
      };

      Object.entries(translationMap).forEach(([key, value]) => {
        if (!value) {
          return;
        }

        const hiddenProperties = `__${key.replace(/ /g, '_').toLowerCase()}`;

        hiddenPropertiesMappedTranslations[hiddenProperties] = value;
      });

      const properties = propertiesSetting
        .split('\n')
        .map((property) => {
          const trimmedProperty = property.trim();
          const trimmedHiddenProperty = `__${trimmedProperty
            .replace(/ /g, '_')
            .toLowerCase()
            .trim()}`;
          const translatedKey =
            this.$string(
              hiddenPropertiesMappedTranslations[trimmedHiddenProperty],
            ) || trimmedProperty;
          const value =
            this.lineItem.properties[trimmedProperty] ||
            this.lineItem.properties[trimmedHiddenProperty];
          if (!value || (typeof value === 'string' && !value.trim().length)) {
            return false;
          }

          return typeof value === 'boolean'
            ? `<div><strong>${translatedKey}</strong></div>`
            : `<div><strong>${translatedKey}</strong>: ${value}</div>`;
        })
        .filter(Boolean);

      return properties.join('');
    },

    /**
     * Compute notification state.
     * @returns {Object}
     */
    notification() {
      if (!this.lineItem || (!this.limitedInventory && !this.error)) {
        return { display: false };
      }

      const notification = {};

      /**
       * Display error message from cart response.
       */
      if (this.error) {
        notification.display = true;
        notification.text = this.error;
        notification.title = '';
        return notification;
      }

      /**
       * Display manual error notifications.
       */
      if (this.lineItem.quantity > this.lineItem.inventory_quantity) {
        notification.display = true;
        notification.title = this.$string(
          'cart.notifications.limited_stock.title',
        );

        notification.text = this.$string(
          'cart.notifications.limited_stock.text',
          {
            pluralise: this.lineItem.inventory_quantity,
            replace: { count: this.lineItem.inventory_quantity },
          },
        );
      } else if (this.lineItem.quantity === this.lineItem.inventory_quantity) {
        notification.display = true;
        notification.title = this.$string(
          'cart.notifications.maximum_quantity.title',
        );

        notification.text = this.$string(
          'cart.notifications.maximum_quantity.text',
          {
            pluralise: this.lineItem.inventory_quantity,
            replace: { count: this.lineItem.inventory_quantity },
          },
        );
      }

      return notification;
    },

    /**
     * Compute variant text.
     * @returns {String}
     */
    variantText() {
      if (this.lineItem.gift_card) {
        return this.$formatMoney(this.lineItem.original_price);
      }

      if (!this.lineItem.options_with_values) {
        return this.lineItem.variant_title;
      }

      return this.lineItem.options_with_values
        .slice()
        .reverse()
        .map((option) => {
          return this.$string('cart.line_item.variant', {
            replace: {
              selected_value: option.value,
            },
          });
        })
        .join(', ');
    },

    /**
     * Show extra bundle item name.
     * Item from the bundle where customers cannot select anything
     */
    bundleExtraItemName() {
      return this.lineItem.properties?.extraBundle?.join(', ') || '';
    },
  },

  watch: {
    /**
     * Watch cart status items to see if item is being updated.
     */
    'cartStatus.items'() {
      if (this.cartStatus.items.includes(this.lineItem.key)) {
        this.state = 'updating';
        return;
      }

      this.state = 'ready';
    },

    /**
     * Watch item quantity to update quantity if updated elsewhere.
     */
    'lineItem.quantity'() {
      this.quantity = this.lineItem.quantity;
    },
  },

  mounted() {
    this.state = 'ready';
    this.quantity = this.lineItem.quantity;
  },

  methods: {
    /**
     * Map Pinia actions.
     */
    ...mapActions(useCartStore, {
      addItem: 'addItem',
      updateItem: 'updateItem',
      removeItem: 'updateItem',
      getCartAttributeCode: 'getCartAttributeCode',
      setDepositItemUpdate: 'setDepositItemUpdate',
    }),

    ...mapActions(useDiscountCampaignsStore, {
      setCodeState: 'setCodeState',
      setErrorType: 'setErrorType',
    }),

    ...mapActions(useToastNotificationsStore, ['addToast']),

    /**
     * Handle quantity update event.
     * @param {String} quantity - New quantity.
     */
    async handleQuantityUpdate(quantity) {
      const oldQuantity = this.quantity;
      const addDeposit =
        this.lineItem.can_deposit && this.$variable('store.country') === 'DE';
      this.quantity = quantity;

      try {
        this.error = '';
        this.state = 'updating';

        if (addDeposit) {
          this.setDepositItemUpdate([
            ...this.depositItemUpdate,
            this.lineItem.variant_id,
          ]);
        }

        if (this.quantity === 0 && addDeposit) {
          await this.addRemoveDepositProducts(this.lineItem.key);
        }

        await this.updateItem([
          {
            id: this.lineItem.key,
            quantity: this.quantity,
          },
        ]);

        if (this.quantity > 0 && addDeposit) {
          await this.addRemoveDepositProducts();
        }

        // Tracking event add/remove qty to item in cart
        if (this.quantity > oldQuantity) {
          this.handleTrackingAddRemoveItem(
            this.quantity - oldQuantity,
            'add_to_cart',
          );
        } else {
          this.handleTrackingAddRemoveItem(
            oldQuantity - this.quantity,
            'remove_from_cart',
          );
        }

        // sets it to the original cart.json quantity
        this.quantity = this.lineItem.quantity;
        this.state = 'ready';
      } catch (error) {
        this.quantity = this.lineItem.quantity;

        // Handle maxQuantity errors
        if (error.maxQuantityError) {
          const message = getMaxQuantityErrorMessage(
            error.maxQuantityType,
            error.maxQuantityValue,
          );

          this.addToast({
            title: message,
            id: 'max-quantity-limit-error',
            type: 'error',
          });
          this.state = 'ready';
          return;
        }

        // Set error state for other errors
        this.state = 'error';

        if (error.description) {
          this.handleErrorMessage(error.description);
          return;
        }

        cnvs.ReportError('Failed to update line item quantity', error);
      }

      if (addDeposit) {
        this.setDepositItemUpdate(
          this.depositItemUpdate.filter(
            (item) => item !== this.lineItem.variant_id,
          ),
        );
      }
    },

    /**
     * Handle remove click.
     */
    async handleRemove() {
      if (this.state === 'updating') {
        return;
      }

      // Tracking event remove item from cart
      this.handleTrackingAddRemoveItem(
        this.lineItem.quantity,
        'remove_from_cart',
      );

      if (
        this.lineItem.can_deposit &&
        this.$variable('store.country') === 'DE'
      ) {
        this.setDepositItemUpdate([
          ...this.depositItemUpdate,
          this.lineItem.variant_id,
        ]);
        await this.addRemoveDepositProducts(this.lineItem.key);
      }

      await this.removeItem(this.lineItem.key);
      this.setDepositItemUpdate(
        this.depositItemUpdate.filter(
          (item) => item !== this.lineItem.variant_id,
        ),
      );

      this.setErrorType('');
      this.setCodeState('ready');
    },

    /**
     * Handle error message.
     * @param {Object} errorDescription - Error description from add request.
     */
    handleErrorMessage(errorDescription) {
      if (typeof errorDescription === 'string') {
        this.error = errorDescription;
        return;
      }

      this.error = Object.values(errorDescription)
        .flat()
        .map((error) => {
          return error.endsWith('.') ? error : `${error}.`;
        })
        .join('<br>');
    },

    /**
     * Updates the amount of deposit product to add or remove
     */
    async addRemoveDepositProducts(productRemoved = false) {
      // Initialize the total deposit amount
      let totalDepositQuantity = 0;
      let depositProduct = null;

      // Loop over every item in the cart
      this.cart.items.forEach((item) => {
        if (this.$variable('product.depositProductID') === `${item.id}`) {
          depositProduct = item.key;
        }

        // Accumulate the total deposit quantity
        if (item.can_deposit && item.key !== productRemoved) {
          totalDepositQuantity += item.quantity * item.can_deposit_amount;
        }
      });

      const depositProductExists = this.cart.items.some(
        (item) => `${item.key}` === depositProduct,
      );

      const removeDepositProduct = async () => {
        if (depositProductExists) {
          await this.removeItem([{ id: depositProduct, quantity: 0 }]);
        }
      };

      if (productRemoved) {
        const filteredItems = this.cart.items.filter((item) => {
          return item.key !== productRemoved && item.key !== depositProduct;
        });
        if (filteredItems.length === 0) {
          await removeDepositProduct();
          return;
        }
      }

      if (totalDepositQuantity > 0) {
        if (depositProductExists) {
          await this.updateItem([
            { id: depositProduct, quantity: totalDepositQuantity },
          ]);
        } else {
          await this.addItem([
            {
              id: this.$variable('product.depositProductID'),
              quantity: totalDepositQuantity,
            },
          ]);
        }
      } else {
        await removeDepositProduct();
      }
    },

    /**
     * Tracking event add qty to item in cart.
     * @param {Number} qty - Quantity of item added
     * @param {String} eventType - Event type can be:
     * add_to_cart, remove_from_cart
     */
    handleTrackingAddRemoveItem(qty, eventType) {
      const price = this.lineItem.price;
      const compareAtPrice = this.lineItem.compare_at_price;
      const productId = this.lineItem.product_id;
      const discountLogic = computeDiscountLogic({
        discountPreview: false,
        onSale: compareAtPrice > price,
        product: { id: productId },
        variant: this.lineItem,
      });
      const campaignDiscount = this.getCampaignDiscount(
        discountLogic,
        'active',
      );
      const discountObject = {
        discountAmount: campaignDiscount?.amount,
        calculationType: campaignDiscount?.calculationType,
        discountCode: this.getCartAttributeCode(),
      };
      const deliveryTime = this.$string('product.delivery_time', {
        replace: { time: this.$variable('product.deliveryTime') },
      });
      const currencyCode = this.$variable('store.currency');

      const item = formatTrackingItem({
        variant: this.lineItem,
        template: this.location,
        discountObject,
        deliveryTime,
        currencyCode,
        quantity: qty,
      });

      cnvs.EventBus.emit(`dataLayer:${eventType}`, {
        event: eventType,
        currency: cnvs.store.currency,
        location: 'Cart View',
        items: [item],
      });
    },
  },
};
</script>

<style lang="scss">
@import './line-item';
</style>
